import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, ArrowRight, Bike, CheckCircle2, Loader2, PackageCheck, RefreshCw, Truck } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';

import { DISPATCH_REASON_COPY } from '../../utils/dispatchReasons';
import DeliveryDirectory from './DeliveryDirectory';
import AssignmentProblemQueue from '../../components/delivery/AssignmentProblemQueue';
import ReturnsDesk from '../../components/delivery/ReturnsDesk';
import RiderReportsPanel from '../../components/delivery/RiderReportsPanel';
import ReceivingHoursHint from '../../components/delivery/ReceivingHoursHint';
import FailedStopDispositionQueue from '../../components/delivery/FailedStopDispositionQueue';
import LogisticsAttention from '../../components/delivery/LogisticsAttention';
import { DeliverySections, ExceptionSections } from '../../components/delivery/LogisticsSections';
import TripRecord from '../../components/delivery/TripRecord';
import OperationalMap from '../../../features/delivery/maps/OperationalMap';
import ShopSelect from '../../../components/ShopSelect';
import { useBusinessAuth } from '../../context/BusinessAuthContext';
import { businessDeliveryOperationsService as deliveryApi } from '../../services/businessDeliveryOperationsService';
import { Button, PageHeader } from '../../../components/ui';

const STATUSES = ['', 'PLANNED', 'READY', 'ACTIVE', 'INTERRUPTED'];

// The sidebar owns the four Logistics workspaces. `view` selects a section
// inside one of them, so every existing ?view= link keeps working.
const DELIVERY_VIEWS = ['trips', 'parcels', 'riders', 'returns'];
const EXCEPTION_VIEWS = ['issues', 'exceptions', 'reports'];
const VIEW_COPY = Object.freeze({
  overview: ['Logistics', 'What needs a decision, what is moving now, and where to continue.'],
  trips: ['Delivery trips', 'Current riders, routes and stop progress.'],
  parcels: ['Parcels', 'Find a parcel and see where it is in delivery.'],
  riders: ['Riders', 'Rider duty, capacity and committed work.'],
  returns: ['Rider returns at the counter', 'Parcels a rider is bringing back to the store. Return requests from customers are handled in Customer Service.'],
  issues: ['Exceptions', 'Delivery problems that need a decision, followed by recent road reports.'],
  exceptions: ['Failed stops', 'Decide what happens after a delivery attempt fails.'],
  reports: ['Road reports', 'Recent problems riders reported from the road.'],
});
const label = (value) => String(value || 'Unavailable').replaceAll('_', ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
const formatDistance = (meters) => meters == null || !Number.isFinite(Number(meters))
  ? 'Distance unavailable'
  : Number(meters) >= 1000 ? `${(Number(meters) / 1000).toFixed(1)} km` : `${Number(meters)} m`;
const formatDuration = (seconds) => seconds == null || !Number.isFinite(Number(seconds))
  ? 'Travel time unavailable'
  : Number(seconds) > 0 && Number(seconds) < 60 ? 'Under 1 min' : `${Math.max(0, Math.round(Number(seconds) / 60))} min`;
const tripStatus = (run) => run.status === 'PLANNED'
  ? (run.pickupStartedAt ? 'Collecting parcels' : 'Preparing pickup')
  : run.status === 'READY' ? 'Ready to deliver' : label(run.status);
const riderAvailability = (rider) => rider.currentRun
  ? `Trip #${rider.currentRun.id} - ${label(rider.currentRun.status)}`
  : rider.dutyState !== 'ON_DUTY' ? 'Not receiving work'
    : Number(rider.load) >= Number(rider.capacity) ? 'At capacity'
      : 'No active trip. Dispatch checks eligibility before assigning work.';

const panel = 'rounded-panel border border-default bg-surface';
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';
const textLink = `font-semibold underline underline-offset-4 ${focusRing}`;

export function summarizeDispatchResult(result = {}) {
  const items = Array.isArray(result.items) ? result.items : [];
  const assigned = Number(result.assigned_count ?? result.assignedCount
    ?? items.filter((item) => item.decision === 'ASSIGNED').length);
  const reviewItems = items.filter((item) => item.decision !== 'ASSIGNED');
  // An unrecognised code must not invent a cause, and must not leak the code
  // either - these reach a shop owner, and a raw identifier is an internal.
  // The old catch-all blamed rider availability for everything, which sent an
  // owner hunting a rider problem when the store simply had no delivery rules.
  const reasons = [...new Set(reviewItems.map((item) => DISPATCH_REASON_COPY[item.decision_reason_code]
    || 'This parcel could not be dispatched, and the reason was not recognised. Retry, and contact your administrator if it continues.'))];
  return {
    message: assigned > 0
      ? `${assigned} ${assigned === 1 ? 'parcel was' : 'parcels were'} given a rider.${reviewItems.length ? ` ${reviewItems.length} could not be - see why below.` : ''}`
      : reviewItems.length
        ? `No parcel could be given a rider (${reviewItems.length} waiting). See why below.`
        : 'Nothing was waiting for a rider.',
    reasons,
    settlementWindow: reviewItems.some((item) => item.decision_reason_code === 'SETTLEMENT_WINDOW_UNAVAILABLE'),
  };
}

export function withoutFleetPositions(board) {
  return { ...board, items: (board.items || []).map((run) => ({
    ...run, tracking: { available: false, freshness: 'STALE_UNAVAILABLE', position: null, capturedAt: null },
  })) };
}

export function deliveryOperationsError(error) {
  if (error?.response?.status === 403) return 'You do not have permission to retry automatic dispatch.';
  if (error?.response?.status === 409) return 'Delivery work changed while dispatch was running. Refresh and review the latest parcel state.';
  return 'Automatic dispatch could not be retried. Check workflow health and try again.';
}

function Restricted({ children }) {
  return (
    <p role="alert" className="rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning">
      {children} Ask the business owner if you need this access.
    </p>
  );
}

export default function DeliveryOperations() {
  const auth = useBusinessAuth();
  const [params,setParams] = useSearchParams();
  const tripId = Number(params.get('trip')) || null;
  const requestedView = params.get('view') || 'overview';
  // A trip is always read inside the trips section, whichever link opened it.
  const view = tripId ? 'trips' : VIEW_COPY[requestedView] ? requestedView : 'overview';
  const workspace = DELIVERY_VIEWS.includes(view) ? 'delivery' : EXCEPTION_VIEWS.includes(view) ? 'exceptions' : 'overview';
  const [viewTitle, viewDescription] = VIEW_COPY[view];
  const status = STATUSES.includes(params.get('status')) ? params.get('status') : '';
  const cursor = /^\d+$/.test(params.get('cursor') || '') ? Number(params.get('cursor')) : null;
  const [tripRevision, setTripRevision] = useState(0);
  const owner = String(auth.role || '').toUpperCase() === 'OWNER';
  const can = (permission) => owner || auth.hasPermission(permission);
  // The board, trips and parcels are one authority. Someone who decides
  // exceptions or receives returns may not hold it, and their sections must
  // still work without it.
  const canReadBoard = can('delivery.dispatch.read.business');
  const canReadRiders = can('delivery.riders.read.business');
  // WP7 target-v2 receiving desk. Receiving a return is its own authority - the
  // section stays hidden for anyone who cannot act on it rather than offering a
  // screen whose every button the server would refuse.
  const canConfirmReturnReceipt = can('delivery.custody.receive.business');
  const canDecideReturns = can('delivery.returns.disposition.business');
  const canManageExceptions = can('delivery.exceptions.manage.business');
  const canReceiveReturns = canConfirmReturnReceipt || canDecideReturns || canManageExceptions;
  const canReviewProofs = can('delivery.proofs.review.business');
  const businessId = Number(auth.businessId);
  const canDispatch = can('delivery.dispatch.execute.business');
  const canManageAssignmentProblems = can('delivery.assignment_problems.manage.business');
  const canReadOrders = can('orders:read');
  const canManageSetup = can('delivery.settings.manage.business');
  const canSettle = can('delivery.cod.remit.business');
  const [attentionRevision, setAttentionRevision] = useState(0);
  const [boardState, setBoard] = useState({ items: [], riders: [], capabilities: {} });
  const board = boardState.businessId === businessId ? boardState : { items: [], riders: [], capabilities: {} };
  const ready = board.ready || [];
  const [selectedRun, setSelectedRun] = useState(null);
  const [tripLoading, setTripLoading] = useState(false);
  const [tripError, setTripError] = useState('');
  const [loading, setLoading] = useState(canReadBoard);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dispatchReasons, setDispatchReasons] = useState([]);
  const [showReceivingHours, setShowReceivingHours] = useState(false);
  const retryRef = useRef(15000);
  const currentBusiness = useRef(businessId);
  const selectedTripIdentity = useRef('');
  currentBusiness.current = businessId;
  const requestSequence = useRef(0);
  const tripSequence = useRef(0);
  const updateParams = useCallback((changes) => setParams((current) => {
    const next = new URLSearchParams(current);
    Object.entries(changes).forEach(([key, value]) => {
      if (value == null || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    return next;
  }), [setParams]);

  // Older links open a trip with no view, or under another one. Rewrite the
  // address to the trips section so the sidebar and Back agree with the page.
  useEffect(() => {
    if (!tripId || requestedView === 'trips') return;
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set('view', 'trips');
      return next;
    }, { replace: true });
  }, [tripId, requestedView, setParams]);

  const load = useCallback(async ({ quiet = false, signal } = {}) => {
    if (!businessId) return;
    if (!canReadBoard) { setLoading(false); return; }
    const sequence = ++requestSequence.current;
    if (!quiet) setLoading(true);
    setBoard(withoutFleetPositions);
    try {
      const nextBoard = await deliveryApi.board(
        businessId,
        { ...(status ? { status } : {}), cursor, limit: 10 },
        { signal },
      );
      if (signal?.aborted || sequence !== requestSequence.current || currentBusiness.current !== businessId) return;
      setBoard({ ...(nextBoard || { items: [], riders: [], ready: [], capabilities: {} }), businessId });
      setError('');
      retryRef.current = 15000;
    } catch (requestError) {
      if (requestError?.code === 'ERR_CANCELED' || sequence !== requestSequence.current || currentBusiness.current !== businessId) return;
      setBoard(withoutFleetPositions);
      if ([401, 403].includes(requestError?.response?.status)) {
        setBoard({ items: [], riders: [], capabilities: {} });
        setSelectedRun(null);
      }
      setError(requestError?.response?.status === 403
        ? 'You do not have permission to view delivery operations.'
        : 'Delivery operations could not refresh. Last-known run details remain; fleet locations are hidden until a fresh update.');
      retryRef.current = Math.min(60000, retryRef.current * 2);
    } finally { if (sequence === requestSequence.current && currentBusiness.current === businessId) setLoading(false); }
  }, [businessId, canReadBoard, status, cursor]);

  useEffect(() => { setBoard({ items: [], riders: [], capabilities: {} }); }, [businessId]);

  // Target-v2 Assignment Problems (dark). A Business with no target work gets an
  // empty list and no surface change at all.
  const [problems, setProblems] = useState([]);
  const [problemsError, setProblemsError] = useState('');
  const [problemsRevision, setProblemsRevision] = useState(0);
  const [problemsLoaded, setProblemsLoaded] = useState(false);
  const [failedStopsRevision, setFailedStopsRevision] = useState(0);
  const [riderReportsRevision, setRiderReportsRevision] = useState(0);

  useEffect(() => {
    if (!businessId || !canManageAssignmentProblems) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const data = await deliveryApi.assignmentProblems(businessId, { status: 'OPEN' }, { signal: controller.signal });
        if (!cancelled) { setProblems(Array.isArray(data) ? data : (data?.items || [])); setProblemsError(''); setProblemsLoaded(true); }
      } catch (problemError) {
        if (cancelled || controller.signal.aborted) return;
        setProblemsLoaded(true);
        if (problemError?.response?.status === 403) { setProblems([]); setProblemsError('Reported problems are unavailable for this account.'); return; }
        setProblemsError('Reported problems could not be loaded. Check your connection and try again.');
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [businessId, canManageAssignmentProblems, problemsRevision]);

  useEffect(() => {
    if (view !== 'overview') return undefined;
    const refreshAttention = () => {
      if (document.visibilityState === 'visible') setAttentionRevision(value => value + 1);
    };
    const timer = window.setInterval(refreshAttention, 60000);
    window.addEventListener('focus', refreshAttention);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refreshAttention); };
  }, [view]);

  const resolveProblem = useCallback(async (problem, decision) => {
    setBusy(true);
    setProblemsError('');
    try {
      await deliveryApi.resolveAssignmentProblem(businessId, problem.assignmentId, problem.id, {
        expectedAssignmentVersion: problem.assignmentVersion,
        ...decision,
      });
      setNotice('The decision was recorded.');
      setProblemsRevision((value) => value + 1);
    } catch (resolveError) {
      setProblemsError(resolveError?.response?.data?.message
        || 'That decision could not be recorded. Refresh and try again.');
    } finally {
      setBusy(false);
    }
  }, [businessId]);

  useEffect(() => {
    const controller = new AbortController();
    load({ signal: controller.signal });
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (!canReadBoard) return undefined;
    let stopped = false;
    let timer;
    const schedule = () => {
      timer = setTimeout(async () => {
        if (!stopped && document.visibilityState === 'visible') await load({ quiet: true });
        if (!stopped) schedule();
      }, retryRef.current);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') load({ quiet: true });
      else setBoard(withoutFleetPositions);
    };
    document.addEventListener('visibilitychange', onVisibility);
    schedule();
    return () => { stopped = true; clearTimeout(timer); document.removeEventListener('visibilitychange', onVisibility); };
  }, [load, canReadBoard]);

  useEffect(()=>{
    if(!tripId||!canReadBoard){tripSequence.current+=1;selectedTripIdentity.current='';setSelectedRun(null);setTripError('');setTripLoading(false);return undefined;}
    const controller=new AbortController();
    const sequence=++tripSequence.current;
    const identity=`${businessId}:${tripId}`;
    const hasCurrentTrip=selectedTripIdentity.current===identity;
    if(!hasCurrentTrip)setSelectedRun(null);
    setTripError('');setTripLoading(!hasCurrentTrip);
    deliveryApi.run(businessId,tripId,{signal:controller.signal}).then(data=>{if(!controller.signal.aborted&&sequence===tripSequence.current){selectedTripIdentity.current=identity;setSelectedRun(data);}}).catch(()=>{if(!controller.signal.aborted&&sequence===tripSequence.current)setTripError('Could not refresh this trip. Check your connection and try again.');}).finally(()=>{if(!controller.signal.aborted&&sequence===tripSequence.current)setTripLoading(false);});
    return ()=>controller.abort();
  },[businessId,tripId,tripRevision,canReadBoard]);
  useEffect(()=>{
    if(!tripId||!canReadBoard)return undefined;
    const refresh=()=>{if(document.visibilityState==='visible')setTripRevision(value=>value+1);};
    const timer=window.setInterval(refresh,10000);
    window.addEventListener('focus',refresh);
    return()=>{window.clearInterval(timer);window.removeEventListener('focus',refresh);};
  },[tripId,canReadBoard]);
  const openRun = (runId) => updateParams({view:'trips',trip:runId});
  const executeDispatch = async () => {
    setBusy(true); setError(''); setNotice(''); setDispatchReasons([]);
    try {
      const result = await deliveryApi.dispatch(businessId);
      const summary = summarizeDispatchResult(result);
      setNotice(summary.message);
      setDispatchReasons(summary.reasons);
      setShowReceivingHours(Boolean(summary.settlementWindow));
      await load({ quiet: true });
    } catch (requestError) {
      setError(deliveryOperationsError(requestError));
    } finally { setBusy(false); }
  };

  const fleetPoints = useMemo(() => (board.items || []).filter((run) => run.tracking?.position)
    .map((run) => ({ ...run.tracking.position, label: `${run.rider?.displayName || 'Rider'} · Trip ${run.id}` })), [board.items]);
  const activeTrips = (board.items || []).filter((run) => run.status === 'ACTIVE');
  const boardShown = board.businessId === businessId;
  const unhealthy = boardShown && board.workflowHealth && board.workflowHealth.state !== 'HEALTHY';
  const refreshPage = () => {
    if (view === 'issues') {
      setProblemsRevision(value => value + 1);
      setFailedStopsRevision(value => value + 1);
      setRiderReportsRevision(value => value + 1);
      return;
    }
    if (view === 'exceptions') {
      setFailedStopsRevision(value => value + 1);
      return;
    }
    if (view === 'reports') {
      setRiderReportsRevision(value => value + 1);
      return;
    }
    load();
    setTripRevision(value => value + 1);
    setProblemsRevision(value => value + 1);
    setAttentionRevision(value => value + 1);
  };
  const boardSpinner = (
    <div role="status" className="grid min-h-48 place-items-center">
      <Loader2 className="h-7 w-7 animate-spin text-accent motion-reduce:animate-none" aria-hidden="true" />
      <span className="sr-only">Loading delivery operations…</span>
    </div>
  );

  return (
    <main className="space-y-5 pb-10">
      <PageHeader
        title={viewTitle}
        description={viewDescription}
        actions={<>
          {view === 'overview' && canManageSetup && <Button as={Link} size="sm" to="/business/settings?tab=delivery">Delivery setup</Button>}
          <Button size="sm" icon={RefreshCw} onClick={refreshPage} disabled={busy}>
            {view === 'issues' ? 'Refresh all' : 'Refresh'}
          </Button>
        </>}
        navigation={<>
          {workspace === 'delivery' && <DeliverySections current={view} />}
          {workspace === 'exceptions' && <ExceptionSections current={view === 'exceptions' ? 'issues' : view} />}
        </>}
      />

      {notice && <p role="status" aria-live="polite" className={`rounded-2xl border p-4 text-sm ${dispatchReasons.length ? 'border-warning bg-warning-subtle text-warning' : 'border-success bg-success-subtle text-success'}`}>{notice}</p>}
      {unhealthy && ['overview', 'trips'].includes(view) && <p role="alert" className="rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning">Automatic delivery processing is unavailable or delayed. Ready parcels remain queued. Contact your administrator to restore it; Retry automatic dispatch can retry assignment now.</p>}
      {dispatchReasons.length > 0 && <div className="rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning"><p className="font-semibold">Why dispatch could not give these parcels a rider</p><ul className="mt-2 list-disc space-y-1 pl-5">{dispatchReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>{showReceivingHours && <ReceivingHoursHint className="mt-3" />}</div>}
      {error && <p role="alert" className="flex gap-2 rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning"><AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />{error}</p>}

      {view === 'overview' && (
        <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          <LogisticsAttention businessId={businessId} board={board} boardLoading={loading} boardError={error} assignmentProblems={problems} problemsError={problemsError} canReadOrders={canReadOrders} canManageExceptions={canManageExceptions} canReceiveReturns={canConfirmReturnReceipt} canDecideReturns={canDecideReturns} canReviewProofs={canReviewProofs} canDecideCancellations={canManageExceptions} revision={attentionRevision} />

          <div className="space-y-4">
            {canReadBoard && (
              <section aria-labelledby="moving-now-title" className={`overflow-hidden ${panel}`}>
                <div className="flex min-h-14 items-center justify-between gap-3 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
                  <h2 id="moving-now-title" className="font-semibold">Moving now</h2>
                  <Link to="/business/delivery?view=trips" className={`inline-flex min-h-9 items-center text-sm ${textLink}`}>All trips</Link>
                </div>
                {loading && !boardShown ? boardSpinner : (
                  <>
                    {activeTrips.length ? (
                      <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                        {activeTrips.slice(0, 5).map((run) => (
                          <li key={run.id}>
                            <Link to={`/business/delivery?view=trips&trip=${run.id}`} className={`group flex min-h-14 items-center gap-3 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-white/5 ${focusRing} focus-visible:ring-inset`}>
                              <Truck className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                              <span className="min-w-0 flex-1">
                                <span className="block truncate font-semibold">{run.rider?.displayName || `Trip #${run.id}`}</span>
                                <span className="block text-sm text-neutral-600 dark:text-neutral-300">Trip #{run.id} · {Number(run.resolvedCount || 0)} of {Number(run.stopCount || 0)} stops done · {run.tracking?.available ? label(run.tracking.freshness) : 'Tracking unavailable'}</span>
                              </span>
                              <ArrowRight className="h-4 w-4 shrink-0 text-neutral-400 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    ) : !error ? <p className="px-4 py-4 text-sm text-neutral-600 dark:text-neutral-300">No trip is on the road right now.</p> : null}
                    {activeTrips.length > 5 && <p className="border-t border-neutral-200 px-4 py-3 text-sm text-neutral-600 dark:border-neutral-800 dark:text-neutral-300">Showing 5 of the trips on this page. Open All trips for the rest.</p>}
                    {!error && (
                      <Link to="/business/delivery?view=trips" className={`flex min-h-14 items-center gap-3 border-t border-neutral-200 px-4 py-3 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-white/5 ${focusRing} focus-visible:ring-inset`}>
                        <PackageCheck className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                        <span className="min-w-0 flex-1 text-sm">
                          <span className="font-semibold">{ready.length} {ready.length === 1 ? 'parcel is' : 'parcels are'} waiting for a rider.</span>{' '}
                          <span className="text-neutral-600 dark:text-neutral-300">Dispatch assigns them automatically.</span>
                        </span>
                        <ArrowRight className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                      </Link>
                    )}
                  </>
                )}
              </section>
            )}

            <section aria-labelledby="logistics-reference-title" className={`p-4 ${panel}`}>
              <h2 id="logistics-reference-title" className="font-semibold">Look something up</h2>
              <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                {[
                  canReadOrders && ['/business/orders', 'Order records'],
                  canReadBoard && ['/business/delivery?view=parcels', 'Parcels'],
                  canReadRiders && ['/business/delivery?view=riders', 'Riders'],
                  canReviewProofs && ['/business/delivery/proofs?show=all', 'Delivery proofs'],
                  canSettle && ['/business/delivery/settlement', 'Settlement desk'],
                ].filter(Boolean).map(([to, title]) => <li key={to}><Link to={to} className={`inline-flex min-h-9 items-center ${textLink}`}>{title}</Link></li>)}
              </ul>
            </section>
          </div>
        </div>
      )}

      {view === 'parcels' && (canReadBoard ? <DeliveryDirectory kind="parcels" /> : <Restricted>You do not have access to parcels.</Restricted>)}
      {view === 'riders' && (canReadRiders ? <DeliveryDirectory kind="riders" /> : <Restricted>You do not have access to the rider directory.</Restricted>)}

      {view === 'returns' && (canReceiveReturns
        ? <ReturnsDesk businessId={businessId} canReceive={canConfirmReturnReceipt} canDecide={canDecideReturns} canReviewCancellations={canManageExceptions} />
        : <Restricted>You do not have access to rider returns.</Restricted>)}

      {view === 'issues' && (canManageExceptions || canManageAssignmentProblems ? (
        <div className="space-y-4">
          {canManageAssignmentProblems && problemsLoaded && (problems.length > 0 || problemsError
            ? <section className={`p-4 sm:p-5 ${panel}`}><AssignmentProblemQueue problems={problems} error={problemsError} busy={busy} onResolve={resolveProblem} onRetry={() => setProblemsRevision(value => value + 1)} /></section>
            : <p className={`flex items-center gap-2 px-4 py-3 text-sm ${panel}`}><CheckCircle2 className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />No rider has reported a problem with assigned work.</p>)}
          {canManageExceptions && <FailedStopDispositionQueue businessId={businessId} refreshRevision={failedStopsRevision} onChanged={() => { load({ quiet: true }); setAttentionRevision(value => value + 1); }} />}
          {canManageExceptions && <RiderReportsPanel businessId={businessId} refreshRevision={riderReportsRevision} />}
        </div>
      ) : <Restricted>You do not have access to delivery exceptions.</Restricted>)}
      {view === 'exceptions' && (canManageExceptions
        ? <FailedStopDispositionQueue businessId={businessId} refreshRevision={failedStopsRevision} onChanged={() => { load({ quiet: true }); setTripRevision((value) => value + 1); }} />
        : <Restricted>You do not have permission to decide failed stops.</Restricted>)}
      {view === 'reports' && (canManageExceptions
        ? <RiderReportsPanel businessId={businessId} refreshRevision={riderReportsRevision} />
        : <Restricted>You do not have permission to see Rider reports.</Restricted>)}

      {view === 'trips' && !canReadBoard && <Restricted>You do not have access to delivery trips.</Restricted>}
      {view === 'trips' && canReadBoard && !tripId && (loading ? boardSpinner : (
        <>
          <section aria-labelledby="trips-title" className={`overflow-hidden ${panel}`}>
            <div className="flex flex-wrap items-end justify-between gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
              <div><h2 id="trips-title" className="font-semibold">Trips</h2><p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300">Route, progress and tracking for each rider.</p></div>
              <div className="flex items-center gap-2 text-sm font-medium">
                <span>Trip status</span>
                <ShopSelect
                  value={status}
                  onValueChange={(value) => updateParams({ status: value, cursor: null })}
                  options={STATUSES.map((item) => ({
                    value: item,
                    label: item ? label(item) : 'All active work',
                  }))}
                  ariaLabel="Trip status"
                  align="end"
                  minPanelWidth={160}
                  triggerClassName="min-h-11 w-40 rounded-control px-3 text-sm font-medium"
                  panelClassName="py-1"
                />
              </div>
            </div>
            {board.items?.length ? (
              <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                {board.items.map((run) => (
                  <li key={run.id}>
                    <button type="button" onClick={() => openRun(run.id)} className={`group grid w-full gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-neutral-50 dark:hover:bg-white/5 sm:grid-cols-[minmax(0,1.4fr)_auto_minmax(0,1.6fr)_auto] sm:items-center ${focusRing} focus-visible:ring-inset`}>
                      <span className="min-w-0"><span className="block truncate font-semibold">{run.rider?.displayName || 'Rider unavailable'}</span><span className="block text-sm text-neutral-600 dark:text-neutral-300">Trip #{run.id} · {Number(run.resolvedCount || 0)} of {Number(run.stopCount || 0)} stops resolved</span></span>
                      <span className="justify-self-start rounded-full bg-neutral-100 px-2.5 py-1 text-xs font-semibold dark:bg-white/10">{tripStatus(run)}</span>
                      <span className="flex flex-wrap gap-x-3 text-xs text-neutral-600 dark:text-neutral-300"><span>{formatDistance(run.route?.distanceM)}</span><span>{formatDuration(run.route?.durationS)}</span><span>{run.tracking?.available ? label(run.tracking.freshness) : 'Tracking unavailable'}</span></span>
                      <ArrowRight className="hidden h-4 w-4 text-neutral-400 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none sm:block" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : <p className="px-4 py-6 text-sm text-neutral-600 dark:text-neutral-300">No delivery trips match this status. Committed parcels will appear here as their pickup route is prepared.</p>}
            {(cursor != null || board.nextCursor != null) && (
              <div className="flex gap-4 border-t border-neutral-200 px-4 py-2 text-sm dark:border-neutral-800">
                {cursor != null && <button type="button" className={`min-h-11 ${textLink}`} onClick={() => updateParams({cursor:null})}>First trips page</button>}
                {board.nextCursor != null && <button type="button" className={`min-h-11 ${textLink}`} onClick={() => updateParams({cursor:board.nextCursor})}>Next trips page</button>}
              </div>
            )}
          </section>

          <div className="grid items-start gap-4 lg:grid-cols-2">
            <section aria-labelledby="ready-title" className={`overflow-hidden ${panel}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
                <div><h2 id="ready-title" className="font-semibold">Ready for dispatch</h2><p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300">{ready.length} confirmed {ready.length === 1 ? 'parcel' : 'parcels'} waiting. Dispatch assigns them automatically after the batching window; retry only when work needs recovery.</p></div>
                {canDispatch && <button type="button" onClick={executeDispatch} disabled={busy || ready.length === 0} className={`business-primary-action inline-flex min-h-11 items-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:ring-offset-2 disabled:opacity-50 ${focusRing}`}><Truck className="h-4 w-4" aria-hidden="true" /> Retry automatic dispatch</button>}
              </div>
              {ready.length ? (
                <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                  {ready.slice(0, 8).map((item) => (
                    <li key={item.shipmentId}><Link to={`/business/delivery?view=parcels&parcel=${item.shipmentId}`} className={`flex min-h-12 items-center justify-between gap-3 px-4 py-2 text-sm hover:bg-neutral-50 dark:hover:bg-white/5 ${focusRing} focus-visible:ring-inset`}><span className="min-w-0"><span className="block truncate font-medium">{item.orderReference || `Delivery ${item.shipmentId}`}</span><span className="block truncate text-neutral-600 dark:text-neutral-300">{item.destinationArea || 'Destination confirmed'}</span></span><PackageCheck className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" /></Link></li>
                  ))}
                </ul>
              ) : <p className="px-4 py-4 text-sm text-neutral-600 dark:text-neutral-300">No parcels are waiting for dispatch.</p>}
              {ready.length > 8 && <p className="border-t border-neutral-200 px-4 py-3 text-sm dark:border-neutral-800"><Link to="/business/delivery?view=parcels" className={textLink}>See all parcels</Link></p>}
            </section>

            {board.capabilities?.readRiders && (
              <section aria-labelledby="rider-availability-title" className={`overflow-hidden ${panel}`}>
                <div className="flex items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
                  <h2 id="rider-availability-title" className="font-semibold">Rider availability</h2>
                  {canReadRiders && <Link to="/business/delivery?view=riders" className={`inline-flex min-h-9 items-center text-sm ${textLink}`}>Rider directory</Link>}
                </div>
                {(board.riders || []).length ? (
                  <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                    {board.riders.map((rider) => (
                      <li key={`${rider.displayName}:${rider.currentRun?.id || 'free'}`} className="flex items-start gap-3 px-4 py-3">
                        <Bike className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
                        <div className="min-w-0"><h3 className="font-medium">{rider.displayName}</h3><p className="text-xs text-neutral-600 dark:text-neutral-300">{label(rider.dutyState)} - {rider.load} committed - Capacity {rider.capacity}</p><p className="mt-1 text-sm">{riderAvailability(rider)}</p></div>
                      </li>
                    ))}
                  </ul>
                ) : <p className="px-4 py-4 text-sm text-neutral-600 dark:text-neutral-300">No riders are set up for delivery yet.</p>}
              </section>
            )}
          </div>

          {board.capabilities?.readTracking && fleetPoints.length > 0 && <section className={`p-4 sm:p-5 ${panel}`}><h2 className="font-semibold">Live fleet map</h2><p className="mt-1 text-sm text-neutral-600 dark:text-neutral-300">Only recent, usable positions from active runs appear.</p><OperationalMap points={fleetPoints} className="mt-4" /></section>}
        </>
      ))}

      {tripId && canReadBoard && <Link to="/business/delivery?view=trips" className={`inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-secondary hover:text-accent ${focusRing}`}><ArrowLeft className="h-4 w-4" aria-hidden="true" />Back to trips</Link>}
      {tripId && tripLoading && !selectedRun && <div role="status" className="grid min-h-48 place-items-center rounded-2xl border border-neutral-200 dark:border-neutral-800"><Loader2 className="h-7 w-7 animate-spin text-accent motion-reduce:animate-none" aria-hidden="true" /><span className="sr-only">Loading trip details...</span></div>}
      {tripId && tripError && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning"><span className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />{tripError}</span><button type="button" onClick={() => setTripRevision(value => value + 1)} className="min-h-11 rounded-xl border border-warning px-4 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Try again</button></div>}

      {selectedRun && selectedRun.businessId === businessId && <TripRecord run={selectedRun} businessId={businessId} can={can} onUpdated={() => setTripRevision(value => value + 1)} />}
    </main>
  );
}
